import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, X, Edit3, Trash2, Archive, ExternalLink,
  FileText, Link2, LayoutTemplate, BookOpen, Palette,
  StickyNote, ClipboardList, Folder, ChevronRight,
  Tag, Clock, Save, ArrowLeft, Grid3X3, List, BookMarked,
} from "lucide-react";
import HandoffBrief from "./HandoffBrief";

// ─── Constants ────────────────────────────────────────────────────────────────

const RESOURCE_TYPES = [
  { id: "document",  label: "Document",   icon: FileText,      color: "#5A90F5", emoji: "📄" },
  { id: "reference", label: "Reference",  icon: Link2,         color: "#00C781", emoji: "🔗" },
  { id: "template",  label: "Template",   icon: LayoutTemplate,color: "#A855F7", emoji: "📋" },
  { id: "guide",     label: "Guide",      icon: BookOpen,      color: "#FF8A00", emoji: "📚" },
  { id: "branding",  label: "Branding",   icon: Palette,       color: "#EC4899", emoji: "🎨" },
  { id: "note",      label: "Note",       icon: StickyNote,    color: "#F59E0B", emoji: "📝" },
  { id: "sop",       label: "SOP",        icon: ClipboardList, color: "#14B8A6", emoji: "📐" },
];

const TYPE_MAP = Object.fromEntries(RESOURCE_TYPES.map(t => [t.id, t]));

function typeInfo(type) {
  return TYPE_MAP[type] || RESOURCE_TYPES[0];
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (d < 1) return "just now";
  if (d < 60) return `${d}m ago`;
  if (d < 1440) return `${Math.floor(d/60)}h ago`;
  return `${Math.floor(d/1440)}d ago`;
}

// ─── New Resource Modal ───────────────────────────────────────────────────────

function NewResourceModal({ onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("document");
  const [desc, setDesc] = useState("");
  const [url, setUrl] = useState("");
  const [folder, setFolder] = useState("");
  const info = typeInfo(type);

  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-[#EDEDED] mb-5">New resource</h3>

        {/* Type picker */}
        <div className="grid grid-cols-4 gap-1.5 mb-4">
          {RESOURCE_TYPES.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setType(t.id)}
                className={`flex flex-col items-center gap-1 p-2 rounded-xl border text-[9px] transition-all ${type === t.id ? "border-transparent text-white" : "border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA]"}`}
                style={type === t.id ? { background: t.color + "25", borderColor: t.color + "60", color: t.color } : {}}>
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Resource title…"
          autoFocus
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[13px] text-[#EDEDED] placeholder-[#52525B] outline-none focus:border-[#5A90F5] mb-3" />

        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Brief description…"
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#EDEDED] placeholder-[#52525B] outline-none resize-none mb-3"
          rows={2} />

        {type === "reference" && (
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…"
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#EDEDED] placeholder-[#52525B] outline-none focus:border-[#5A90F5] mb-3" />
        )}

        <input value={folder} onChange={e => setFolder(e.target.value)} placeholder="Folder (optional)…"
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#EDEDED] placeholder-[#52525B] outline-none focus:border-[#5A90F5] mb-5" />

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-[12px] border border-[#2A2A2A] text-[#71717A]">Cancel</button>
          <button
            onClick={() => { if (title.trim()) onSave({ title: title.trim(), type, description: desc, url: url || null, folder: folder || null }); }}
            disabled={!title.trim()}
            className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold text-white disabled:opacity-30 transition-colors"
            style={{ background: info.color }}>
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Resource Editor (full-screen within the environment) ────────────────────

function ResourceEditor({ resource, wsId, onClose, onUpdate }) {
  const [title, setTitle] = useState(resource.title);
  const [content, setContent] = useState(resource.content || "");
  const [description, setDescription] = useState(resource.description || "");
  const [url, setUrl] = useState(resource.url || "");
  const [tags, setTags] = useState(resource.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const info = typeInfo(resource.type);
  const Icon = info.icon;

  const save = useCallback(async (fields = {}) => {
    setSaving(true);
    try {
      const patch = { title, content, description, url: url || null, tags, ...fields };
      const { data } = await api.put(`/workspaces/${wsId}/resources/${resource.resource_id}`, patch);
      onUpdate(data);
      setDirty(false);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  }, [wsId, resource.resource_id, title, content, description, url, tags, onUpdate]);

  // Auto-save after 1.5s of inactivity
  useEffect(() => {
    if (!dirty) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(), 1500);
    return () => clearTimeout(saveTimer.current);
  }, [dirty, title, content, description, url, tags]);

  const mark = () => setDirty(true);

  const addTag = () => {
    if (!tagInput.trim() || tags.includes(tagInput.trim())) return;
    const next = [...tags, tagInput.trim()];
    setTags(next); setTagInput(""); mark();
  };

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      {/* Editor header */}
      <div className="flex items-center gap-3 px-8 py-4 border-b border-[#1A1A1A] flex-shrink-0">
        <button onClick={() => { if (dirty) save(); onClose(); }}
          className="p-1.5 rounded-lg hover:bg-[#141414] text-[#71717A] hover:text-[#A1A1AA] transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: info.color + "25" }}>
          <Icon className="w-3.5 h-3.5" style={{ color: info.color }} />
        </div>
        <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: info.color }}>{info.label}</span>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {saving && <span className="text-[10px] text-[#52525B]">Saving…</span>}
          {dirty && !saving && <span className="text-[10px] text-[#52525B]">Unsaved</span>}
          <button onClick={() => save()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-semibold text-white transition-colors"
            style={{ background: info.color }}>
            <Save className="w-3 h-3" /> Save
          </button>
        </div>
      </div>

      {/* Editor body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-8 py-8">
          {/* Title */}
          <input
            value={title}
            onChange={e => { setTitle(e.target.value); mark(); }}
            className="w-full bg-transparent text-[28px] font-bold text-[#EDEDED] placeholder-[#2A2A2A] outline-none mb-3"
            placeholder="Resource title…"
          />

          {/* Description */}
          <input
            value={description}
            onChange={e => { setDescription(e.target.value); mark(); }}
            className="w-full bg-transparent text-[14px] text-[#71717A] placeholder-[#2A2A2A] outline-none mb-6 border-b border-[#1A1A1A] pb-6"
            placeholder="Add a description…"
          />

          {/* URL for references */}
          {resource.type === "reference" && (
            <div className="mb-6">
              <label className="text-[10px] text-[#52525B] uppercase tracking-wider block mb-2">URL</label>
              <div className="flex items-center gap-2 p-3 bg-[#111] border border-[#2A2A2A] rounded-xl">
                <Link2 className="w-3.5 h-3.5 text-[#52525B] flex-shrink-0" />
                <input value={url} onChange={e => { setUrl(e.target.value); mark(); }}
                  placeholder="https://…"
                  className="flex-1 bg-transparent text-[12px] text-[#5A90F5] placeholder-[#52525B] outline-none" />
                {url && (
                  <a href={url} target="_blank" rel="noreferrer" className="text-[#52525B] hover:text-[#A1A1AA]">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="mb-6">
            <label className="text-[10px] text-[#52525B] uppercase tracking-wider block mb-2">Content</label>
            <textarea
              value={content}
              onChange={e => { setContent(e.target.value); mark(); }}
              placeholder={
                resource.type === "guide" ? "Step 1: …\nStep 2: …\nStep 3: …" :
                resource.type === "sop" ? "Purpose:\n\nProcedure:\n\nNotes:" :
                resource.type === "template" ? "Enter your template content here…" :
                resource.type === "branding" ? "Primary color: …\nFonts: …\nLogo: …" :
                "Write your content here…"
              }
              className="w-full bg-[#111] border border-[#2A2A2A] rounded-xl px-4 py-3 text-[13px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none resize-none leading-relaxed focus:border-[#2A2A2A] transition-colors font-mono"
              rows={16}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="text-[10px] text-[#52525B] uppercase tracking-wider block mb-2">Tags</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map(t => (
                <span key={t} className="flex items-center gap-1 px-2 py-0.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full text-[10px] text-[#A1A1AA]">
                  {t}
                  <button onClick={() => { setTags(tags.filter(x => x !== t)); mark(); }} className="text-[#52525B] hover:text-[#EF4444]">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
                placeholder="Add tag…"
                className="flex-1 bg-[#111] border border-[#2A2A2A] rounded-xl px-3 py-1.5 text-[11px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5]" />
              <button onClick={addTag} className="px-3 py-1.5 rounded-xl bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#71717A] hover:text-[#EDEDED] text-[11px]">Add</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Resource Card ────────────────────────────────────────────────────────────

function ResourceCard({ resource, view, onOpen, onDelete, onArchive }) {
  const info = typeInfo(resource.type);
  const Icon = info.icon;
  const [menu, setMenu] = useState(false);

  if (view === "list") {
    return (
      <div className="group flex items-center gap-3 px-4 py-3 bg-[#111] border border-[#2A2A2A] rounded-xl hover:border-[#3A3A3A] transition-all cursor-pointer"
        onClick={onOpen}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: info.color + "20" }}>
          <Icon className="w-4 h-4" style={{ color: info.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-[#EDEDED] truncate">{resource.title}</p>
          {resource.description && <p className="text-[11px] text-[#71717A] truncate">{resource.description}</p>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {resource.folder && <span className="text-[9px] text-[#52525B] flex items-center gap-1"><Folder className="w-2.5 h-2.5" />{resource.folder}</span>}
          {resource.tags?.slice(0,2).map(t => <span key={t} className="text-[9px] px-1.5 py-0.5 bg-[#1A1A1A] text-[#52525B] rounded-full border border-[#2A2A2A]">{t}</span>)}
          <span className="text-[10px] text-[#3A3A3A] flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{timeAgo(resource.updated_at)}</span>
          <div className="relative">
            <button onClick={e => { e.stopPropagation(); setMenu(v => !v); }}
              className="p-1.5 rounded-lg hover:bg-[#1A1A1A] text-[#3A3A3A] hover:text-[#A1A1AA] opacity-0 group-hover:opacity-100 transition-all">
              <Edit3 className="w-3 h-3" />
            </button>
            {menu && (
              <div className="absolute right-0 top-8 z-50 bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl p-1 w-36 text-xs"
                onClick={e => e.stopPropagation()} onMouseLeave={() => setMenu(false)}>
                <button onClick={() => { onOpen(); setMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#1A1A1A] text-[#EDEDED]"><Edit3 className="w-3 h-3" /> Edit</button>
                <button onClick={() => { onArchive(); setMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#1A1A1A] text-[#A1A1AA]"><Archive className="w-3 h-3" /> Archive</button>
                <div className="h-px bg-[#2A2A2A] my-1" />
                <button onClick={() => { onDelete(); setMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#EF4444]/10 text-[#EF4444]"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="group relative bg-[#111] border border-[#2A2A2A] rounded-2xl p-4 hover:border-[#3A3A3A] transition-all cursor-pointer flex flex-col gap-3"
      onClick={onOpen}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
          style={{ background: info.color + "20" }}>
          {resource.emoji || <Icon className="w-5 h-5" style={{ color: info.color }} />}
        </div>
        <div className="relative">
          <button onClick={e => { e.stopPropagation(); setMenu(v => !v); }}
            className="p-1.5 rounded-lg hover:bg-[#1A1A1A] text-[#3A3A3A] hover:text-[#A1A1AA] opacity-0 group-hover:opacity-100 transition-all">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          {menu && (
            <div className="absolute right-0 top-8 z-50 bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl p-1 w-36 text-xs"
              onClick={e => e.stopPropagation()} onMouseLeave={() => setMenu(false)}>
              <button onClick={() => { onOpen(); setMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#1A1A1A] text-[#EDEDED]"><Edit3 className="w-3 h-3" /> Edit</button>
              <button onClick={() => { onArchive(); setMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#1A1A1A] text-[#A1A1AA]"><Archive className="w-3 h-3" /> Archive</button>
              <div className="h-px bg-[#2A2A2A] my-1" />
              <button onClick={() => { onDelete(); setMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#EF4444]/10 text-[#EF4444]"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          )}
        </div>
      </div>
      <div>
        <p className="text-[13px] font-semibold text-[#EDEDED] mb-1 line-clamp-1">{resource.title}</p>
        {resource.description && <p className="text-[11px] text-[#71717A] line-clamp-2">{resource.description}</p>}
      </div>
      {resource.content && (
        <p className="text-[10px] text-[#3A3A3A] line-clamp-3 font-mono">{resource.content.slice(0, 120)}</p>
      )}
      <div className="flex items-center justify-between mt-auto pt-1">
        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: info.color + "20", color: info.color }}>{info.label}</span>
        <span className="text-[9px] text-[#3A3A3A]">{timeAgo(resource.updated_at)}</span>
      </div>
      {resource.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {resource.tags.slice(0,3).map(t => <span key={t} className="text-[9px] px-1.5 py-0.5 bg-[#1A1A1A] text-[#52525B] rounded-full border border-[#2A2A2A]">{t}</span>)}
        </div>
      )}
    </div>
  );
}

// ─── Main Resources page ──────────────────────────────────────────────────────

export default function Resources() {
  const { current } = useWorkspace();
  const wsId = current?.workspace_id;
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterFolder, setFilterFolder] = useState(null);
  const [view, setView] = useState("grid"); // grid | list
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [subtab, setSubtab] = useState("resources"); // resources | handoff

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/resources`);
      setResources(data || []);
    } catch {} finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { load(); }, [wsId]);

  const create = async (body) => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/resources`, body);
      setResources(r => [data, ...r]);
      setShowNew(false);
      setEditing(data);
    } catch { toast.error("Failed to create"); }
  };

  const update = (updated) => {
    setResources(r => r.map(x => x.resource_id === updated.resource_id ? updated : x));
  };

  const archive = async (id) => {
    try {
      await api.put(`/workspaces/${wsId}/resources/${id}`, { archived: true });
      setResources(r => r.filter(x => x.resource_id !== id));
      if (editing?.resource_id === id) setEditing(null);
    } catch {}
  };

  const del = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/resources/${id}`);
      setResources(r => r.filter(x => x.resource_id !== id));
      if (editing?.resource_id === id) setEditing(null);
    } catch {}
  };

  // Get unique folders
  const folders = [...new Set(resources.map(r => r.folder).filter(Boolean))].sort();

  // Filter resources
  const visible = resources.filter(r => {
    if (filterType !== "all" && r.type !== filterType) return false;
    if (filterFolder && r.folder !== filterFolder) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.title.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q) || r.tags?.some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  // If editing, show the editor
  if (editing) {
    return (
      <ResourceEditor
        resource={editing}
        wsId={wsId}
        onClose={() => setEditing(null)}
        onUpdate={(updated) => { update(updated); setEditing(updated); }}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-[#1A1A1A] flex-shrink-0">
        {/* Sub-nav */}
        <div className="flex gap-1 mr-2">
          {[["resources","Resources"],["handoff","Handoff Briefs"]].map(([id, label]) => (
            <button key={id} onClick={() => setSubtab(id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${subtab === id ? "bg-[#1A1A1A] text-[#EDEDED]" : "text-[#71717A] hover:text-[#A1A1AA]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        {subtab === "resources" && (
          <>
            {/* Search */}
            <div className="flex items-center gap-2 bg-[#141414] border border-[#2A2A2A] rounded-xl px-3 py-1.5 w-52">
              <Search className="w-3.5 h-3.5 text-[#52525B] flex-shrink-0" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search resources…"
                className="flex-1 bg-transparent text-[11px] text-[#EDEDED] placeholder-[#52525B] outline-none" />
              {search && <button onClick={() => setSearch("")} className="text-[#52525B] hover:text-[#A1A1AA]"><X className="w-3 h-3" /></button>}
            </div>
            {/* View toggle */}
            <div className="flex gap-0.5 bg-[#141414] border border-[#2A2A2A] rounded-lg p-0.5">
              <button onClick={() => setView("grid")} className={`p-1.5 rounded ${view === "grid" ? "bg-[#2A2A2A] text-[#EDEDED]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}><Grid3X3 className="w-3.5 h-3.5" /></button>
              <button onClick={() => setView("list")} className={`p-1.5 rounded ${view === "list" ? "bg-[#2A2A2A] text-[#EDEDED]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}><List className="w-3.5 h-3.5" /></button>
            </div>
            {/* New */}
            <button onClick={() => setShowNew(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-semibold text-white bg-[#5A90F5] hover:bg-[#4A80E5] transition-colors">
              <Plus className="w-3.5 h-3.5" /> New
            </button>
          </>
        )}
      </div>

      {/* Handoff briefs sub-tab */}
      {subtab === "handoff" && (
        <div className="flex-1 min-h-0">
          <HandoffBrief embedded />
        </div>
      )}

      {/* Resources sub-tab */}
      {subtab === "resources" && (
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Sidebar */}
          <div className="w-48 flex-shrink-0 border-r border-[#1A1A1A] flex flex-col overflow-y-auto py-3">
            {/* All */}
            <button onClick={() => { setFilterType("all"); setFilterFolder(null); }}
              className={`w-full flex items-center gap-2 px-4 py-2 text-left text-[11px] transition-colors ${filterType === "all" && !filterFolder ? "bg-[#141414] text-[#EDEDED]" : "text-[#71717A] hover:text-[#A1A1AA] hover:bg-[#0F0F0F]"}`}>
              <BookMarked className="w-3.5 h-3.5" /> All Resources
              <span className="ml-auto text-[9px] text-[#3A3A3A]">{resources.length}</span>
            </button>

            {/* By type */}
            <div className="px-4 py-2 mt-1">
              <p className="text-[9px] uppercase tracking-wider text-[#3A3A3A] mb-1.5">By type</p>
              {RESOURCE_TYPES.map(t => {
                const Icon = t.icon;
                const count = resources.filter(r => r.type === t.id).length;
                if (count === 0) return null;
                return (
                  <button key={t.id} onClick={() => { setFilterType(t.id); setFilterFolder(null); }}
                    className={`w-full flex items-center gap-2 py-1.5 px-2 rounded-lg text-[11px] transition-colors mb-0.5 ${filterType === t.id && !filterFolder ? "text-[#EDEDED]" : "text-[#71717A] hover:text-[#A1A1AA]"}`}
                    style={filterType === t.id && !filterFolder ? { background: t.color + "20", color: t.color } : {}}>
                    <Icon className="w-3 h-3" style={filterType === t.id && !filterFolder ? { color: t.color } : {}} />
                    {t.label}
                    <span className="ml-auto text-[9px] opacity-60">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Folders */}
            {folders.length > 0 && (
              <div className="px-4 py-2 mt-1">
                <p className="text-[9px] uppercase tracking-wider text-[#3A3A3A] mb-1.5">Folders</p>
                {folders.map(f => (
                  <button key={f} onClick={() => { setFilterFolder(f); setFilterType("all"); }}
                    className={`w-full flex items-center gap-2 py-1.5 px-2 rounded-lg text-[11px] transition-colors mb-0.5 ${filterFolder === f ? "bg-[#141414] text-[#EDEDED]" : "text-[#71717A] hover:text-[#A1A1AA]"}`}>
                    <Folder className="w-3 h-3" /> {f}
                    <span className="ml-auto text-[9px] text-[#3A3A3A]">{resources.filter(r => r.folder === f).length}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="flex items-center justify-center h-32 text-[#3A3A3A] text-[12px]">Loading…</div>
            ) : visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#3A3A3A]" />
                </div>
                <div className="text-center">
                  <p className="text-[14px] font-semibold text-[#52525B]">
                    {search ? "No results" : "No resources yet"}
                  </p>
                  <p className="text-[12px] text-[#3A3A3A] mt-1">
                    {search ? "Try a different search" : "Create your first resource to get started"}
                  </p>
                </div>
                {!search && (
                  <button onClick={() => setShowNew(true)}
                    className="px-5 py-2.5 rounded-xl text-[12px] font-semibold bg-[#5A90F5] hover:bg-[#4A80E5] text-white transition-colors">
                    Create resource
                  </button>
                )}
              </div>
            ) : view === "grid" ? (
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
                {visible.map(r => (
                  <ResourceCard key={r.resource_id} resource={r} view="grid"
                    onOpen={() => setEditing(r)}
                    onDelete={() => del(r.resource_id)}
                    onArchive={() => archive(r.resource_id)} />
                ))}
              </div>
            ) : (
              <div className="space-y-2 max-w-3xl">
                {visible.map(r => (
                  <ResourceCard key={r.resource_id} resource={r} view="list"
                    onOpen={() => setEditing(r)}
                    onDelete={() => del(r.resource_id)}
                    onArchive={() => archive(r.resource_id)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showNew && <NewResourceModal onClose={() => setShowNew(false)} onSave={create} />}
    </div>
  );
}
